import { AlertSeverity } from '@rover/react-lib/src/components/notifications/Alert/Alert';
import {
  AvailableForSelectedDatesEnum,
  OptimizedSearchResult,
} from '@rover/rsdk/src/apiClient/latest';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';

import {
  AlertContentEnum,
  getAlertContent,
  getSubHeader,
  HEADER_TITLES,
  HeaderIconEnum,
  HeaderTitlesEnum,
  SubHeadersEnum,
} from './constants';
import { BaseHeaderBuilderProps, PrebuiltHeaderProps } from './types';

interface Props extends BaseHeaderBuilderProps {
  isBestMatchesSearch?: boolean;
  selectedProvider: OptimizedSearchResult;
}

const buildSelectedProviderHeader = (props: Props): PrebuiltHeaderProps | object => {
  const { startDate, endDate } = props.dateData;
  const availability =
    props.selectedProvider.availableForSelectedDates || AvailableForSelectedDatesEnum.unknown;
  const isUnavailable = availability === AvailableForSelectedDatesEnum.unavailable;
  const hasDates = startDate && endDate;
  const stringProps = {
    ...props,
    sitterName: props.selectedProvider.shortName,
  };

  const title =
    HEADER_TITLES()[
      isUnavailable
        ? HeaderTitlesEnum.SELECTED_SITTER_UNAVAILABLE_TITLE
        : HeaderTitlesEnum.SELECTED_SITTER_TITLE
    ];

  let primarySubheader;
  let secondarySubheader;
  let alertText;

  if (props.priceTransparencyLevel === PriceTransparencyLevels.none) {
    if (hasDates) {
      if (availability === AvailableForSelectedDatesEnum.available) {
        primarySubheader = getSubHeader(SubHeadersEnum.SELECTED_SITTER_AVAILABLE, stringProps);
      } else if (availability === AvailableForSelectedDatesEnum.unavailable) {
        primarySubheader = getSubHeader(SubHeadersEnum.SELECTED_SITTER_UNAVAILABLE, stringProps);
      } else {
        primarySubheader = getSubHeader(SubHeadersEnum.SELECTED_SITTER_NO_DATES, stringProps);
      }
    } else {
      primarySubheader = getSubHeader(SubHeadersEnum.SELECTED_SITTER_NO_DATES, stringProps);
    }
  } else if (!hasDates) {
    // Price transparency, no dates
    if (props.priceTransparencyLevel === PriceTransparencyLevels.full) {
      alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_ADD_DATES, stringProps);
    } else if (props.priceTransparencyLevel === PriceTransparencyLevels.lite) {
      alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_LITE_ADD_DATES, stringProps);
    }
  } else {
    // Price transparency, has dates
    // eslint-disable-next-line no-lonely-if
    if (props.priceTransparencyLevel === PriceTransparencyLevels.full) {
      alertText = getAlertContent(
        isUnavailable
          ? AlertContentEnum.PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_UNAVAILABLE
          : AlertContentEnum.PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_AVAILABLE,
        stringProps
      );
    } else if (props.priceTransparencyLevel === PriceTransparencyLevels.lite) {
      alertText = getAlertContent(
        isUnavailable
          ? AlertContentEnum.PRICE_TRANSPARENCY_LITE_SELECTED_SITTER_UNAVAILABLE
          : AlertContentEnum.PRICE_TRANSPARENCY_LITE_SELECTED_SITTER_AVAILABLE,
        stringProps
      );
    }
  }

  if (props.isBestMatchesSearch && props.selectedProvider.isBestMatch && !isUnavailable) {
    if (props.priceTransparencyLevel !== PriceTransparencyLevels.none) {
      primarySubheader = getSubHeader(
        SubHeadersEnum.BEST_MATCH_SELECTED_PROVIDER_SHORT_LEAD_TIME,
        stringProps
      );
    } else {
      secondarySubheader = getSubHeader(
        SubHeadersEnum.BEST_MATCH_SELECTED_PROVIDER_SHORT_LEAD_TIME,
        stringProps
      );
    }
  }

  return {
    icon: HeaderIconEnum.PERSON,
    title,
    primarySubheader,
    secondarySubheader,
    alertText,
    alertSeverity: startDate && endDate ? AlertSeverity.INFO : AlertSeverity.WARNING,
    isBestMatchHeader: false,
  };
};

export default buildSelectedProviderHeader;
